/* ==========================================================================
   Quick-add buy box (CRO style) - behaviour for sections/quick-add-product.liquid
   - Size + purchase option (one-time / subscribe / multi-buy) selection
   - Adds to cart via /cart/add.js and dispatches Horizon's CartAddEvent, so the
     cart count + drawer refresh and the quick-add dialog closes, like the native form
   The element can be morphed in place when the same product is reopened, so all
   state is read from the DOM on demand rather than cached on connect.
   ========================================================================== */
import { CartAddEvent } from '@theme/events';

const formatMoney = (cents, format) => {
  const fmt = format || '${{amount}}';
  const value = Number(cents) || 0;
  const fixed = (n, d, thou = ',', dec = '.') => {
    const parts = (n / 100).toFixed(d).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
    return parts[1] ? parts[0] + dec + parts[1] : parts[0];
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return fixed(value, 0);
      case 'amount_with_comma_separator':
        return fixed(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fixed(value, 0, '.', ',');
      default:
        return fixed(value, 2);
    }
  });
};

/** "0.68 fl oz" -> 0.68, anything else -> null */
const flOz = (title) => {
  const m = /([\d.]+)\s*fl\.?\s*oz/i.exec(title || '');
  return m ? Number(m[1]) : null;
};

const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

class QuickAddBuybox extends HTMLElement {
  connectedCallback() {
    if (this.dataset.qabBound) {
      this.render();
      return;
    }
    this.dataset.qabBound = 'true';

    this.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      if (t.matches('[data-qab-size]')) this.onVariant();
      if (t.matches('[data-qab-opt], [data-qab-freq-select]')) this.render();
    });

    this.addEventListener('submit', (e) => {
      if (!(e.target instanceof HTMLFormElement) || !e.target.matches('[data-qab-form]')) return;
      e.preventDefault();
      this.submit();
    });

    this.render();
  }

  get data() {
    const el = this.querySelector('script[data-qab-product]');
    if (!el) return { variants: [] };
    if (this._raw !== el.textContent) {
      this._raw = el.textContent;
      this._data = JSON.parse(el.textContent);
    }
    return this._data;
  }

  get form() {
    return this.querySelector('form[data-qab-form]');
  }

  get variant() {
    const { variants } = this.data;
    const checked = this.querySelector('[data-qab-size]:checked');
    const id = checked ? Number(checked.value) : Number(this.form?.querySelector('[name="id"]')?.value);
    return variants.find((v) => v.id === id) || variants.find((v) => v.available) || variants[0];
  }

  get option() {
    const checked = this.querySelector('[data-qab-opt]:checked');
    return checked ? checked.value : 'once';
  }

  get bundlePaid() {
    return Number(this.dataset.bundlePaid) || 2;
  }

  get bundleTotal() {
    return Number(this.dataset.bundleTotal) || 3;
  }

  onVariant() {
    const v = this.variant;
    const img = this.querySelector('[data-qab-image]');
    if (v && v.image && img instanceof HTMLImageElement) {
      img.removeAttribute('srcset');
      img.src = v.image;
    }
    this.render();
  }

  render() {
    const v = this.variant;
    const form = this.form;
    if (!v || !form) return;

    const fmt = (c) => formatMoney(c, this.data.money_format);
    const set = (sel, text) => this.querySelectorAll(sel).forEach((el) => (el.textContent = text));
    const plans = v.selling_plans || [];
    const hasPlans = plans.length > 0;

    // Unit prices on size cards
    this.data.variants.forEach((variant) => {
      const oz = flOz(variant.title);
      set(`[data-qab-unit="${variant.id}"]`, oz ? `${fmt(Math.round(variant.price / oz))} / fl oz` : '');
    });

    // Subscribe availability for this variant
    const subInput = this.querySelector('[data-qab-opt][value="sub"]');
    if (subInput instanceof HTMLInputElement) {
      subInput.disabled = !hasPlans;
      const label = subInput.closest('[data-qab-option]');
      if (label instanceof HTMLElement) label.hidden = !hasPlans;
      if (!hasPlans && subInput.checked) {
        const once = this.querySelector('[data-qab-opt][value="once"]');
        if (once instanceof HTMLInputElement) once.checked = true;
      }
    }

    // Frequency options for this variant
    const freqWrap = this.querySelector('[data-qab-freq]');
    const freq = this.querySelector('[data-qab-freq-select]');
    if (freq instanceof HTMLSelectElement && hasPlans) {
      const ids = plans.map((p) => p.id);
      const same = freq.options.length === ids.length && [...freq.options].every((o, i) => Number(o.value) === ids[i]);
      if (!same) {
        const current = Number(freq.value);
        freq.replaceChildren(
          ...plans.map((p) => {
            const o = document.createElement('option');
            o.value = String(p.id);
            o.textContent = p.name;
            return o;
          })
        );
        if (ids.includes(current)) freq.value = String(current);
      }
    }

    const option = this.option;
    const planId = freq instanceof HTMLSelectElement ? Number(freq.value) : null;
    const plan = plans.find((p) => p.id === planId) || plans[0];
    if (freqWrap instanceof HTMLElement) freqWrap.hidden = option !== 'sub';

    // Option prices
    set('[data-qab-price="once"]', fmt(v.price));
    if (plan) {
      set('[data-qab-price="sub"]', fmt(plan.price));
      set('[data-qab-was="sub"]', plan.price < v.price ? fmt(v.price) : '');
    }
    set('[data-qab-price="bundle"]', fmt(v.price * this.bundlePaid));
    set('[data-qab-was="bundle"]', fmt(v.price * this.bundleTotal));

    // Headline price
    let now = v.price;
    let was = null;
    if (option === 'sub' && plan) {
      now = plan.price;
      was = plan.price < v.price ? v.price : null;
    } else if (option === 'bundle') {
      now = v.price * this.bundlePaid;
      was = v.price * this.bundleTotal;
    }
    set('[data-qab-head-price]', fmt(now));
    const headWas = this.querySelector('[data-qab-head-was]');
    if (headWas instanceof HTMLElement) {
      headWas.hidden = was == null;
      headWas.textContent = was == null ? '' : fmt(was);
    }
    const oz = flOz(v.title);
    set('[data-qab-head-unit]', oz && option === 'once' ? `${fmt(Math.round(v.price / oz))} / fl oz` : '');

    // Form fields
    const qty = option === 'bundle' ? this.bundleTotal : 1;
    const idInput = form.querySelector('[name="id"]');
    const qtyInput = form.querySelector('[name="quantity"]');
    const spInput = form.querySelector('[name="selling_plan"]');
    if (idInput instanceof HTMLInputElement) idInput.value = String(v.id);
    if (qtyInput instanceof HTMLInputElement) qtyInput.value = String(qty);
    if (spInput instanceof HTMLInputElement) {
      spInput.value = option === 'sub' && plan ? String(plan.id) : '';
      spInput.disabled = !(option === 'sub' && plan);
    }

    // Button
    const cta = this.querySelector('[data-qab-cta]');
    if (cta instanceof HTMLButtonElement && cta.getAttribute('aria-busy') !== 'true') {
      let label;
      if (!v.available) label = this.dataset.textSoldOut || 'Sold out';
      else if (option === 'sub' && plan) label = `${this.dataset.textSubscribe || 'Subscribe'} · ${fmt(now)}`;
      else label = `${this.dataset.textAdd || 'Add to cart'} · ${fmt(now)}`;
      cta.disabled = !v.available;
      set('[data-qab-cta-label]', label);
    }
  }

  async submit() {
    const v = this.variant;
    const form = this.form;
    const cta = this.querySelector('[data-qab-cta]');
    const errorEl = this.querySelector('[data-qab-error]');
    if (!v || !v.available || !form || !(cta instanceof HTMLButtonElement)) return;

    const fd = new FormData(form);
    const option = this.option;
    /** @type {{id: number, quantity: number, selling_plan?: number}} */
    const item = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity')) || 1 };
    if (option === 'sub' && fd.get('selling_plan')) item.selling_plan = Number(fd.get('selling_plan'));

    if (errorEl) errorEl.textContent = '';
    cta.setAttribute('aria-busy', 'true');
    cta.disabled = true;

    try {
      const sections = cartSectionIds();
      const url = (window.Theme?.routes?.cart_add_url || '/cart/add').replace(/\.js$/, '') + '.js';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(sections.length ? { items: [item], sections: sections.join(',') } : { items: [item] }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart');

      const label = this.querySelector('[data-qab-cta-label]');
      if (label) label.textContent = this.dataset.textAdded || 'Added';

      // Same event the native product form dispatches: refreshes cart + closes the quick-add dialog
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'product-form-component',
          itemCount: item.quantity,
          productId: String(this.data.product_id),
          sections: json.sections,
        })
      );

      window.dataLayer?.push({
        event: 'quick_add_to_cart',
        qa_option: option,
        variant_id: item.id,
        quantity: item.quantity,
      });
    } catch (err) {
      if (errorEl) errorEl.textContent = err instanceof Error ? err.message : 'Could not add to cart';
    } finally {
      setTimeout(() => {
        cta.removeAttribute('aria-busy');
        this.render();
      }, 1200);
    }
  }
}

if (!customElements.get('quick-add-buybox')) customElements.define('quick-add-buybox', QuickAddBuybox);
